import {
  inject,
  onBeforeMount,
  provide,
  ref,
  watch,
  computed,
  nextTick,
} from "vue";
import { storeToRefs } from "pinia";
import { uniqBy } from "lodash";
import { useRoute } from "vue-router";
import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { httpClientOmni } from "@/libraries";

import notificationBrowser from "@/helpers/notification-browser";
import getDecodedUserId from "@/helpers/get-decoded-user-id";
import { getAppId } from "@/helpers/app-id";

import useChatPanelFilter from "@/store/pinia/use-chat-panel-filter";

export default function useChat() {
  const socket = inject("socket");
  const receiveAudios = inject("receiveAudios");

  const filterStore = useChatPanelFilter();
  const { filter } = storeToRefs(filterStore);

  const route = useRoute();

  const selectedChat = ref(null);
  const chatList = ref([]);
  const messagesFromUser = ref(null);
  const pinnedIds = ref([]);
  const pinnedChat = ref([]);

  const queryClient = useQueryClient();

  // Mutation for marking messages as read
  const mutationReadMessage = useMutation({
    mutationFn: async ({ roomId, lastMessageId }) => {
      const response = await httpClientOmni.post(`message/room-read-message`, {
        room_id: roomId,
        last_message_id: lastMessageId,
      });
      return response.data;
    },
  });

  // Helper function to mark room as read with optimistic update
  const markRoomAsRead = (roomId, lastMessageId) => {
    if (!roomId || !lastMessageId) return;

    // Store previous state for rollback
    const chatIndex = chatList.value.findIndex((item) => item.id === roomId);
    const previousUnreadCount =
      chatIndex > -1 ? chatList.value[chatIndex].message_unread_count : 0;
    const previousSelectedUnreadCount =
      selectedChat.value?.message_unread_count || 0;

    // Optimistic update: immediately set unread counts to 0
    if (chatIndex > -1) {
      chatList.value[chatIndex].unread_count = 0;
      chatList.value[chatIndex].message_unread_count = 0;
    }
    if (selectedChat.value?.id === roomId) {
      selectedChat.value.unread_count = 0;
      selectedChat.value.message_unread_count = 0;
    }

    // Call the API
    mutationReadMessage.mutate(
      { roomId, lastMessageId },
      {
        onError: () => {
          // Rollback optimistic update on error
          // Find the current index by roomId since chatList may have been reordered
          const currentChatIndex = chatList.value.findIndex(
            (item) => item.id === roomId
          );
          if (currentChatIndex > -1) {
            chatList.value[currentChatIndex].unread_count = previousUnreadCount;
            chatList.value[currentChatIndex].message_unread_count =
              previousUnreadCount;
          }
          if (selectedChat.value?.id === roomId) {
            selectedChat.value.unread_count = previousSelectedUnreadCount;
            selectedChat.value.message_unread_count =
              previousSelectedUnreadCount;
          }
        },
      }
    );
  };

  const perPage = 20;

  const isLastPage = ref(false);

  const roomId = computed(() => route.query?.id);

  const queryGetChatList = useInfiniteQuery({
    queryKey: ["QUERY_GET_CHAT_LIST", filter],
    queryFn: async ({ pageParam }) => {
      const roomId = new URLSearchParams(window.location.search).get("id");
      const res = await httpClientOmni.post("/room/get-room-list-v3", {
        ...filter.value,
        room_id: roomId || "",
        page: pageParam,
      });
      if (res.data.data.length === 0) {
        isLastPage.value = true;
      } else {
        isLastPage.value = false;
      }
      if (roomId) {
        selectedChat.value = res.data.data[0];
      }
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (_, pages) => {
      return pages.length + 1;
    },
  });

  useQuery({
    queryKey: ["QUERY_GET_PINNED"],
    queryFn: async () => {
      const response = await httpClientOmni.get("/room/pin");
      pinnedIds.value = response.data.data.map((item) => item.room_id);
      return response.data;
    },
  });

  const mutationUpdatePinned = useMutation({
    mutationKey: ["MUTATION_UPDATE_PINNED"],
    mutationFn: async ({ roomId, action }) => {
      await httpClientOmni.post(`/room/${action}`, {
        room_id: roomId,
      });

      if (action === "pin") {
        pinnedIds.value.push(roomId);
        pinnedChat.value = [
          ...pinnedChat.value,
          chatList.value.find((item) => item.id === roomId),
        ];
      } else {
        pinnedIds.value = pinnedIds.value.filter((item) => item !== roomId);
        pinnedChat.value = pinnedChat.value.filter(
          (item) => item.id !== roomId
        );
      }
    },
  });

  const unpinnedChat = computed(() => {
    const flatten = (chatList.value || []).filter(
      (item) => !pinnedIds.value.includes(item.interactions?.room_id)
    );
    const uniq = uniqBy(flatten, "interactions.room_id");
    const sorted = [...uniq].sort(
      (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
    );
    return sorted;
  });

  // const pinnedChat = computed(() => {
  //   const flatten = (chatList?.value || []).filter((item) =>
  //     pinnedIds.value.includes(item.interactions?.room_id)
  //   );
  //   return uniqBy(flatten, "interactions.room_id");
  // });

  const fetchNextPage = () => {
    if (
      !queryGetChatList.isFetchingNextPage.value &&
      !isLastPage.value &&
      (queryGetChatList.data.value?.pages || []).flatMap((page) => page.data)
        .length >= perPage
    ) {
      nextTick(() => {
        queryGetChatList.fetchNextPage();
      });
    }
  };

  const isEmpty = computed(() => {
    return (
      unpinnedChat.value.length === 0 &&
      pinnedChat.value.length === 0 &&
      !queryGetChatList.isFetching.value &&
      !queryGetChatList.isLoading.value
    );
  });

  watch(
    () => queryGetChatList.data.value,
    (val) => {
      chatList.value = val?.pages.flatMap((page) => page.data);
    }
  );

  watch([() => chatList.value, () => pinnedIds.value], ([list, pin]) => {
    const flatten = (list || []).filter((item) =>
      pin.includes(item.interactions?.room_id)
    );
    if (flatten.length === pin.length) {
      pinnedChat.value = uniqBy(flatten, "interactions.room_id");
    }
  });

  watch(
    () => roomId.value,
    () => {
      queryGetChatList.refetch();
    }
  );

  onBeforeMount(() => {
    socket?.on("NewMessage", (data) => {
      const newChatFromSocket = {
        app_id: data.app.id,
        channel: data.room.channel,
        channel_id: data.room.channel.id,
        created_by: data.room.created_by,
        created_at: data.room.created_at,
        handle_by: {
          ...data.room.livechat,
          // name: data.user?.name,
        },
        id: data.room.id,
        livechat: data.room.livechat,
        interactions: {
          // Use room.created_at as fallback for interactions.created_at if not provided
          // This ensures WhatsApp expiry calculation works correctly for new chats
          created_at:
            data.room.interactions?.created_at || data.room.created_at || "",
          id: data.room.interactions?.id || "",
          message: 1,
          room_id: data.room.id,
        },
        integration: {
          business_unit_id: data.room.integration.business_unit_id,
          ...(data.room.integration?.integration_data || {}),
        },
        messages: [data.message],
        tags: data.room.tag,
        unread_count: 0,
        message_unread_count: 0,
        updated_at: data.room.updated_at,
      };

      const isChannelIncluded =
        filter.value.channel?.includes(data.room?.channel?.name) ||
        filter.value.channel?.length === 0;

      const isStatutsIncluded =
        data.room?.livechat?.status === filter.value.status ||
        filter.value.status === "";

      const isBussinessUnitIncluded =
        filter.value.business_unit.includes(
          data.room.integration?.business_unit_id
        ) || filter.value.business_unit.length === 0;

      const isTagIncluded =
        filter.value.tags.length === 0 ||
        data.room.tags
          .map((tag) => filter.value.tags.includes(tag.name))
          .every(Boolean);

      const isNameIncluded =
        data.user.name.toLowerCase().includes(filter.value.name) ||
        filter.value.name === "";

      const shouldShowChat =
        isChannelIncluded &&
        isStatutsIncluded &&
        isBussinessUnitIncluded &&
        isTagIncluded &&
        isNameIncluded;

      // If the incoming message belongs to the room currently opened
      // in the chat panel, always handle it regardless of the sidebar filter,
      // so the conversation view stays live.
      const isCurrentRoom = String(data.room.id) === String(roomId.value || "");

      if (shouldShowChat || isCurrentRoom) {
        const appId = getAppId();
        const roomId = data.room.id;
        const userId = getDecodedUserId();
        const role = queryClient.getQueryData(["QUERY_GET_ROLE"]);
        const isAgent = role?.isAgent;

        const isInChatPanel = window.location.pathname.includes(
          "/omni/conversation/chat"
        );

        /**
         * Check if the message is sent by user and not in chat panel page.
         * Only notify the assigned agent (or admin), not other agents.
         */
        const matchAgentForNotification =
          data.room.livechat?.handle_by === userId;
        const isServedForNotification = data.room.livechat?.status === "live";
        const shouldNotify =
          data.message.messageable_type === "user" &&
          !isInChatPanel &&
          (!isAgent || (isServedForNotification && matchAgentForNotification));

        if (shouldNotify) {
          notificationBrowser({
            onClick: () => {
              const url = `${
                import.meta.env.VITE_APP_URL
              }/${appId}/omni/conversation/chat?id=${roomId}`;

              window.location.href = url;
            },
            title: `${data.app.name} | ${data.room.created_by.name}`,
            body:
              data.message.content[0]?.text || data.message.content[0]?.type,
            icon: data.room.created_by.picture,
          });
        }

        const bots = ["bot", "assistant", "agent"];

        const isFromAnotherAgent =
          data.message.messageable_type === "user_platform";
        const isNotCurrentUser = data.user.id !== userId;
        const isFromBot = bots.includes(data.message.messageable_type);
        const isFromUser = data.message.messageable_type === "user";

        const isAbleToReceiveMessage =
          isFromAnotherAgent || isFromBot || isFromUser;
        const isServed = data.room.livechat?.status === "live";
        const isBot = !data.room.livechat;
        const matchAgent = data.room.livechat?.handle_by === userId;

        if (isAgent) {
          // Agent view:
          // - For livechat: only the assigned agent sees messages
          //   Allow customer msgs, bot msgs, and msgs from OTHER platform users
          //   (admins), but block the agent's own echoed messages.
          // - For bot-only rooms (no livechat): always show messages
          const shouldSeeAgentMessageInLivechat =
            isServed && matchAgent && (!isFromAnotherAgent || isNotCurrentUser);
          const isBotOnlyRoom = isBot;

          if (shouldSeeAgentMessageInLivechat || isBotOnlyRoom) {
            messagesFromUser.value = data.message;
          }
        } else {
          // Admin sees messages from everyone (assigned agent or user)
          if (isNotCurrentUser) {
            messagesFromUser.value = data.message;
          }
        }

        // If the chat room is currently opened and message is from another user,
        // automatically mark it as read with optimistic update
        const isChatRoomOpened = selectedChat.value?.id === data.room.id;
        if (isChatRoomOpened && isNotCurrentUser && data.message?.id) {
          markRoomAsRead(data.room.id, data.message.id);
        }
        if (isAbleToReceiveMessage) {
          const upcomingChat = chatList.value.find(
            (item) => item.id === newChatFromSocket.id
          );
          if (upcomingChat) {
            // agent pov when assign changed
            if (isAgent && !matchAgent && isServed) {
              // remove the room from list
              chatList.value = chatList.value.filter(
                (item) => item.id !== data.room?.id
              );
              if (selectedChat.value.id === data.room?.id) {
                // close opened chat
                selectedChat.value = null;
              }
              return;
            }
            const index = chatList.value.findIndex(
              (item) => item.id === newChatFromSocket.id
            );
            // Don't increment unread count if the chat room is currently opened
            const isChatRoomCurrentlyOpened =
              selectedChat.value?.id === newChatFromSocket.id;
            const shouldIncrementUnreadCount =
              isNotCurrentUser && !isChatRoomCurrentlyOpened;

            // When customer sends a new message, reset WhatsApp 24-hour
            // expiry window by updating interactions.created_at to now.
            const newInteractionTime = isFromUser
              ? new Date().toISOString()
              : null;

            const cloneCurrentChatList = {
              ...chatList.value[index],
              ...(newInteractionTime && {
                interactions: {
                  ...chatList.value[index].interactions,
                  created_at: newInteractionTime,
                },
              }),
              messages: [
                // ...chatList.value[index].messages,
                ...newChatFromSocket.messages,
              ],
              unread_count: shouldIncrementUnreadCount
                ? upcomingChat.message_unread_count + 1
                : upcomingChat.message_unread_count,
              message_unread_count: shouldIncrementUnreadCount
                ? upcomingChat.message_unread_count + 1
                : upcomingChat.message_unread_count,
              updated_at: new Date(),
            };

            chatList.value = [
              cloneCurrentChatList,
              ...chatList.value.filter(
                (item) => item.id !== newChatFromSocket.id
              ),
            ];

            // Replace the entire selectedChat ref value so Vue's reactivity
            // fully triggers the whatsapp expiry computed to re-evaluate
            if (isChatRoomCurrentlyOpened) {
              selectedChat.value = { ...cloneCurrentChatList };
            }
          } else {
            // Only add new chat to the assigned agent's list, not other agents
            if (isAgent && isServed && matchAgent) {
              chatList.value = [newChatFromSocket, ...chatList.value];
            }
            if (!isAgent && isBot) {
              chatList.value = [newChatFromSocket, ...chatList.value];
            }
          }

          /**
           * Play audio when receive new message
           */
          if (isInChatPanel) {
            const receiveAudio =
              receiveAudios.find(
                (r) => r.slug === receiveAudios.notificationSound
              ) || receiveAudios[0];

            if (data.room.livechat && userId === data.room.livechat.handle_by) {
              receiveAudio?.control?.play();
            }
          }
        }
      }
    });
  });

  provide("selectedChat", selectedChat);
  provide("messagesFromUser", messagesFromUser);
  provide("providerChatList", {
    queryGetChatList,
    mutationUpdatePinned,
    isEmpty,
    fetchNextPage,
    pinnedChat,
    unpinnedChat,
    chatList,
  });
}
